/**
 * The budget panel — the part of this app that replaces the email loop.
 *
 * Three things it must do, because they are what the current process gets wrong
 * (docs/07-ui-screens.md):
 *
 *   1. Show the pool **before** anyone commits money.
 *   2. Name every attendee who contributes nothing, **and why**.
 *   3. Show participation against the guideline, rather than leaving the manager to count heads.
 *
 * And one thing it must not do: read as a refusal when it is a warning. Five departments here
 * have over 140 people, so a real team outing is short of 50% every time; copy that says
 * "failed" would teach those managers the app forbids what the policy allows (D-21).
 */
import { Money } from '@/components/bits';
import { IssueList } from '@/components/bits';
import { Spinner } from '@/components/ui';
import { formatRatio } from '@/lib/money';
import type { Calculation } from '@/lib/types';

export function BudgetPanel({
  calculation,
  pending,
  error,
  requestedCents,
  children,
}: {
  calculation: Calculation | undefined;
  pending: boolean;
  error: unknown;
  requestedCents?: number | null;
  /** The requested-amount control, so the panel owns the layout and the form owns the state. */
  children?: React.ReactNode;
}) {
  if (error) {
    const message = error instanceof Error ? error.message : String(error);
    return (
      <div className="border-destructive/50 text-destructive rounded-lg border p-5 text-sm">
        <p className="font-medium">The figures could not be calculated</p>
        <p className="mt-1">{message}</p>
      </div>
    );
  }

  if (!calculation) {
    return (
      <div className="border-border text-muted-foreground rounded-lg border p-5 text-sm">
        {pending ? (
          <p className="flex items-center gap-2">
            <Spinner /> Working out the budget…
          </p>
        ) : (
          <p>Pick who is coming and the budget appears here.</p>
        )}
      </div>
    );
  }

  const { participation, attendees, pool_cents, contributing, non_contributing } = calculation;
  const reasons = summariseNonContribution(attendees);

  return (
    <div className="border-border bg-card rounded-lg border">
      <div className="border-border flex items-center justify-between border-b px-5 py-3">
        <h2 className="text-sm font-semibold">Budget</h2>
        {pending ? <Spinner /> : null}
      </div>

      <dl className="grid gap-3 px-5 py-4 text-sm">
        <Row label="Attendees">
          {attendees.length} total, {participation.in_department} from the department
        </Row>

        <Row label="Participation">
          <span className="tabular">
            {participation.in_department} of {participation.department_headcount}
          </span>
          <span className="tabular ml-3 font-medium">{formatRatio(participation.ratio)}</span>
          <span
            className={
              participation.below_threshold
                ? 'ml-2 text-[var(--color-warning)]'
                : 'ml-2 text-[var(--color-success)]'
            }
          >
            {participation.below_threshold
              ? `below the ${Math.round(participation.threshold * 100)}% guideline (R-13)`
              : `at or above the ${Math.round(participation.threshold * 100)}% guideline (R-13)`}
          </span>
          {participation.out_of_department > 0 ? (
            <span className="text-muted-foreground mt-0.5 block text-xs">
              + {participation.out_of_department} from other teams, spending their own entitlement
              (D-33)
            </span>
          ) : null}
        </Row>

        <Row label="Contributing">
          <span className="tabular">{contributing}</span>
          {non_contributing > 0 ? (
            <span className="text-muted-foreground ml-3 text-xs">
              {non_contributing} contribute S$0 — {reasons}
            </span>
          ) : null}
        </Row>
      </dl>

      <div className="border-border border-t px-5 py-4">
        <div className="flex items-baseline justify-between gap-4">
          <span className="text-sm font-medium">Budget available</span>
          <span className="text-lg font-semibold">
            <Money cents={pool_cents} />
          </span>
        </div>

        {children ? <div className="mt-3">{children}</div> : null}

        {requestedCents !== null &&
        requestedCents !== undefined &&
        requestedCents < pool_cents &&
        pool_cents > 0 ? (
          // Encouraged, and explained — otherwise "why is it letting me ask for less?" becomes
          // an email.
          <p className="text-muted-foreground mt-2 text-xs">
            <Money cents={pool_cents - requestedCents} muted /> stays available for a second event
            this year.
          </p>
        ) : null}
      </div>

      {calculation.issues.length > 0 ? (
        <div className="border-border border-t px-5 py-4">
          <IssueList issues={calculation.issues} />
        </div>
      ) : null}
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid gap-x-4 sm:grid-cols-[9rem_1fr]">
      <dt className="text-muted-foreground">{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

/**
 * "1 resigned, 1 under 3 months" — the counts, in the words a manager would use.
 *
 * A total alone ("2 contribute S$0") invites the wrong conclusion, which is that the app has
 * made a mistake.
 */
function summariseNonContribution(attendees: Calculation['attendees']): string {
  const counts = new Map<string, number>();
  for (const attendee of attendees) {
    if (attendee.contributes) continue;
    const wording =
      attendee.reason === 'exited'
        ? 'no longer employed'
        : attendee.reason === 'serving_notice'
          ? 'serving notice'
          : attendee.reason === 'under_three_months'
            ? 'under 3 months'
            : attendee.reason.replace(/_/g, ' ');
    counts.set(wording, (counts.get(wording) ?? 0) + 1);
  }
  return [...counts.entries()].map(([wording, count]) => `${count} ${wording}`).join(', ');
}
